(function () {
  'use strict';

  // Buy-any-3 builder (sections/strip-bundle.liquid, mode "builder") + "+ Add" on collection cards
  // (sections/collection-strip.liquid). Tins go to the cart as one-time lines; the store's automatic
  // discounts (2+ / 3+ eligible tins, mixed products allowed) set the final price in cart.

  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', {
    style: 'currency', currency: currency || 'USD'
  }).format(Number(cents) / 100);
  const addUrl = (url) => { const u = url || '/cart/add'; return u.endsWith('.js') ? u : u + '.js'; };
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('strip-bundle')) {
    class StripBundle extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready) return;
        this.dataset.ready = '1';
        this.products = Array.from(this.querySelectorAll('[data-sb-product]')).map((el) => ({
          key: el.dataset.key, id: el.dataset.variantId, name: el.dataset.name, img: el.dataset.img
        }));
        this.slotEls = Array.from(this.querySelectorAll('[data-sb-slot]'));
        this.slots = this.products.slice(0, 3).map((p) => p.key);   // prefilled: one of each
        while (this.slots.length < 3) this.slots.push(null);
        this.note = this.querySelector('[data-sb-note]');
        this.button = this.querySelector('[data-sb-add]');
        this.querySelectorAll('[data-sb-chip]').forEach((chip) => chip.addEventListener('click', () => this.add(chip.dataset.sbChip, true)));
        this.button.addEventListener('click', () => this.addToCart());
        window.FuseStripBundle = this;
        this.render();
      }

      product(key) { return this.products.find((p) => p.key === key); }
      count() { return this.slots.filter(Boolean).length; }

      total(n) {
        const single = Number(this.dataset.single);
        if (n >= 3) return single * n - Number(this.dataset.d3);
        if (n === 2) return single * 2 - Number(this.dataset.d2);
        return single * n;
      }

      say(text) { if (this.note) this.note.textContent = text; }

      add(key, fromChip) {
        const p = this.product(key);
        if (!p) return 'missing';
        const free = this.slots.indexOf(null);
        if (free === -1) {
          this.say('Your 3 tins are set. Tap a tin to remove it, then add another.');
          this.classList.remove('is-full-flash'); void this.offsetWidth; this.classList.add('is-full-flash');
          return 'full';
        }
        this.slots[free] = key;
        this.render();
        this.say(p.name + ' added. ' + this.remainingText());
        if (fromChip) this.slotEls[free].querySelector('button')?.focus({ preventScroll: true });
        return 'added';
      }

      remove(index) {
        const key = this.slots[index];
        if (!key) return;
        this.slots[index] = null;
        // keep filled slots first so the empty one is always last
        this.slots = this.slots.filter(Boolean).concat(this.slots.filter((s) => !s));
        this.render();
        this.say(this.product(key).name + ' removed. ' + this.remainingText());
        const firstChip = this.querySelector('[data-sb-chip]');
        firstChip?.focus({ preventScroll: true });
      }

      remainingText() {
        const left = 3 - this.count();
        if (left <= 0) return 'Bundle complete: you save ' + money(Number(this.dataset.d3), this.dataset.currency) + '.';
        return 'Add ' + left + ' more to save ' + money(Number(this.dataset.d3), this.dataset.currency) + '.';
      }

      render() {
        const cur = this.dataset.currency;
        this.slotEls.forEach((li, i) => {
          const key = this.slots[i];
          const p = key && this.product(key);
          li.classList.toggle('is-filled', Boolean(p));
          li.innerHTML = '';
          const b = document.createElement('button');
          b.type = 'button';
          if (p) {
            b.className = 'sb-slot__btn sb-slot__btn--' + p.key;
            b.setAttribute('aria-label', 'Remove ' + p.name + ' (tin ' + (i + 1) + ' of 3)');
            b.innerHTML = '<img src="' + p.img + '" width="240" height="240" alt=""><span class="sb-slot__x" aria-hidden="true">×</span>';
            b.addEventListener('click', () => this.remove(i));
          } else {
            b.className = 'sb-slot__btn sb-slot__btn--empty';
            b.setAttribute('aria-label', 'Empty tin ' + (i + 1) + ' of 3. Choose a formula below.');
            b.innerHTML = '<span aria-hidden="true">+</span>';
            b.addEventListener('click', () => this.querySelector('[data-sb-chip]')?.focus());
          }
          li.appendChild(b);
        });
        const n = this.count();
        this.querySelectorAll('.sb-progress span').forEach((dot, i) => dot.classList.toggle('is-on', i < n));
        const label = this.querySelector('[data-sb-label]');
        const total = this.querySelector('[data-sb-total]');
        if (label) label.textContent = n ? 'Add ' + n + (n === 1 ? ' tin' : ' tins') + ' to cart' : 'Choose your tins';
        if (total) total.textContent = n ? money(this.total(n), cur) : '';
        this.button.disabled = n === 0;
      }

      async addToCart() {
        if (!window.FuseCartQuantity || this.button.getAttribute('aria-busy') === 'true') return;
        const counts = {};
        this.slots.filter(Boolean).forEach((k) => { counts[k] = (counts[k] || 0) + 1; });
        const keys = Object.keys(counts);
        if (!keys.length) return;
        const label = this.querySelector('[data-sb-label]');
        const original = label.textContent;
        this.button.setAttribute('aria-busy', 'true');
        this.button.disabled = true;
        label.textContent = 'Adding...';
        let added = 0;
        try {
          for (const k of keys) {
            const fd = new FormData();
            fd.append('id', this.product(k).id);
            fd.append('quantity', String(counts[k]));
            await window.FuseCartQuantity.add(addUrl(this.dataset.cartAddUrl), fd);
            added += counts[k];
          }
          this.say('Added to your cart.');
          label.textContent = 'Added';
          window.Wisp?.showToast?.(added + (added === 1 ? ' tin' : ' tins') + ' added to cart');
          window.Wisp?.openCart?.();
        } catch (error) {
          if (error.addConfirmed) { this.say('Added. Open your cart to see the final price.'); window.Wisp?.openCart?.(); }
          else this.say(added ? added + ' tin(s) were added, but the rest could not be added. Check your cart.' : (error.message || 'Could not add to cart. Try again.'));
          label.textContent = 'Try again';
        } finally {
          window.setTimeout(() => {
            this.button.removeAttribute('aria-busy');
            label.textContent = original;
            this.render();
          }, 1200);
        }
      }
    }
    customElements.define('strip-bundle', StripBundle);
  }

  // "+ Add" on collection cards: fill the builder when it exists, otherwise add one tin.
  if (!window.__stripHomeCards) {
    window.__stripHomeCards = true;
    document.addEventListener('click', async (event) => {
      const btn = event.target.closest('[data-sc-add]');
      if (!btn) return;
      const status = document.querySelector('[data-sc-status]');
      const bundle = window.FuseStripBundle;
      if (bundle) {
        const result = bundle.add(btn.dataset.scAdd);
        if (status) status.textContent = result === 'added' ? btn.dataset.name + ' added to your 3 tins.' : 'Your 3 tins are set. Tap a tin to swap it.';
        bundle.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      if (!window.FuseCartQuantity || btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      const text = btn.textContent;
      btn.textContent = 'Adding...';
      try {
        const fd = new FormData();
        fd.append('id', btn.dataset.variantId);
        fd.append('quantity', '1');
        await window.FuseCartQuantity.add(addUrl(btn.dataset.cartAddUrl), fd);
        btn.textContent = 'Added';
        if (status) status.textContent = btn.dataset.name + ' added to your cart.';
        window.Wisp?.openCart?.();
      } catch (error) {
        btn.textContent = 'Try again';
        if (status) status.textContent = 'Could not add ' + btn.dataset.name + '. Try again.';
      } finally {
        window.setTimeout(() => { btn.removeAttribute('aria-busy'); btn.textContent = text; }, 1300);
      }
    });
  }
})();
